/** @file clip.setMeta: merges the timing, loop, category and facing fields of a clip. */
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { CommandDef } from '../apply/commandDef';
import { clipInverse } from '../apply/clipInverse';
import { checkClip } from '../puppetDoc/checkClip';
import { findClip } from '../puppetDoc/findClip';
import { compact } from '../validate/compact';
import { readFacingSteps } from '../validate/readFacingSteps';
import { readInput } from '../validate/readInput';

const FIELDS = ['fps', 'frames', 'loopStart', 'start', 'loop', 'mirrorHalf', 'category', 'facingSteps'] as const;

function readFields(value: unknown, path: string) {
  const r = readInput(value, path, FIELDS);
  const frame = (k: string): number => r.int(k, { min: 0 });
  return compact({
    fps: r.opt('fps', (k) => r.num(k, { above: 0 })), frames: r.opt('frames', (k) => r.int(k, { min: 1 })),
    loopStart: r.opt('loopStart', frame), start: r.opt('start', frame), loop: r.opt('loop', r.bool), mirrorHalf: r.opt('mirrorHalf', r.bool),
    category: r.opt('category', r.str), facingSteps: r.opt('facingSteps', (k) => readFacingSteps(r.raw(k), r.at(k))),
  });
}

/**
 * `clip.setMeta { clip, fields }` → `{}`: keys above `frames` or a `loopStart`/`start` past the end are
 * `FRAME_OUT_OF_RANGE`. Inverse: the previous values when every field existed, else C.
 */
export const clipSetMetaCommand: CommandDef<'clip.setMeta'> = {
  name: 'clip.setMeta',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['clip', 'fields']);
    return { clip: r.str('clip'), fields: readFields(r.raw('fields'), r.at('fields')) };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const { name, clip } = findClip(draft, input.clip);
    const keys = Object.keys(input.fields) as (keyof Clip)[];
    const inverse = keys.every((k) => clip[k] !== undefined)
      ? [{ name: 'clip.setMeta' as const, input: { clip: name, fields: Object.fromEntries(keys.map((k) => [k, clip[k]])) } }]
      : [clipInverse(clip)];
    Object.assign(clip, input.fields);
    checkClip(name, clip);
    return { result: {}, warnings: [], inverse };
  },
};
